import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Input, TextArea } from '@trackman/web-shared-components';
import ImageUploader from 'components/Admin/ImageUploader/ImageUploader';
import { useServices, useStores } from 'index';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { getErrorMessage } from 'utilities/ErrorHelper';
import useSetPageTitle from 'utilities/useSetPageTitle';

export const EditMaster = () => {
  useSetPageTitle({ title: 'Edit Master', subtitle: '' });

  const queryClient = useQueryClient();
  const masterId = useParams().masterId;

  const notificationsStore = useStores().notificationStore;
  const mastersEditorService = useServices().mastersEditorService;
  const { data: adminMaster } = useQuery({
    queryKey: ['adminMaster', masterId],
    queryFn: async () => {
      const response = await mastersEditorService.getMaster(masterId ?? '');
      setFullName(response.fullName);
      setDescription(response.description);
      setImageUrl(response.imageUrl ?? '');
      return response;
    },
    enabled: !!masterId,
    refetchOnWindowFocus: false,
  });

  const [fullName, setFullName] = useState(adminMaster?.fullName ?? '');
  const [description, setDescription] = useState(adminMaster?.description ?? '');
  const [imageUrl, setImageUrl] = useState<string>(adminMaster?.imageUrl ?? '');
  const [imgFile, setImgFile] = useState<File | undefined>(undefined);

  const handleUploadImage = (data: File) => {
    setImgFile(data);
  };

  const uploadedImage = imgFile ? { id: '', data: imgFile } : undefined;

  const saveMaster = useMutation({
    mutationFn: () =>
      !!masterId ? mastersEditorService.saveExistingMaster(existingMasterPayload) : mastersEditorService.saveNewMaster(newMasterPayload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['masters'] });
      queryClient.refetchQueries({ queryKey: ['masters'] });
      notificationsStore.addToast(
        'success',
        !!masterId ? 'Existing master has been saved successfully' : 'New master has been saved successfully',
        'success'
      );
    },
    onError: (ex) => notificationsStore.addToast('error', getErrorMessage(ex), 'error'),
  });

  const newMasterPayload = {
    file: uploadedImage,
    fullName,
    description,
  };

  const existingMasterPayload = {
    id: masterId!,
    file: uploadedImage,
    fullName,
    description,
    imageUrl,
  };

  return (
    <div className='admin-content-container'>
      <div className='admin-edit-section'>
        <h2>Master's name</h2>
        <div className='inputs-container'>
          <div className='input-wrapper'>
            <Input label={`Full name`} value={fullName} onChange={(e) => setFullName(e.currentTarget.value)} />
          </div>
        </div>
      </div>

      <div className='admin-edit-section'>
        <h2>Description</h2>
        <TextArea value={description} onChange={(e) => setDescription(e.currentTarget.value)} />
      </div>

      <div className='admin-edit-section'>
        <h2>Master's image</h2>
        <ImageUploader imageData={uploadedImage} imageUrl={imageUrl} handleUploadImage={handleUploadImage} />
      </div>
      <div className='admin-footer'>
        <button
          className='styled-button primary'
          onClick={() => saveMaster.mutate()}
          disabled={fullName === '' || (!imageUrl && imgFile === undefined)}
        >
          Save changes
        </button>
      </div>
    </div>
  );
};
